/**
 * Scrape urls to retrieve information about them.
 *
 * Returns a Promise that resolves with the JSON response from the appropriate
 * Scraper.urls entry as an object literal.
 *
 * Results are cached, and a same request done simultaneously will be
 * queued to return the same info as the already-running request.
 */

import queryString from 'query-string'

import { env } from '~publish/env'

import { removeUrlProtocol } from './StringUtils'

export type ResultType = {
  title: string
  description: string
  canonicalUrl: string
  images: {
    url: string
    width: number
    height: number
    type: string
    opengraph?: boolean
  }[]
  success?: boolean
  message?: string
}

const urls = {
  local: 'https://scraper.local.buffer.com',
  production: 'https://scraper.buffer.com',
} as const

const pendingRequests = new Map<string, Promise<ResultType>>() // Stores pending scraper promises

const cache = new Map<string, ResultType>() // Stores results

function scrape(
  url: string,
  isTwitterLinkPreview = false,
): Promise<ResultType> {
  const environment =
    env.VITE_NODE_ENV === 'production' ? 'production' : 'local'

  const urlWithoutProtocol = removeUrlProtocol(url)
  // If the result is cached, return it right away
  const cacheKey = isTwitterLinkPreview
    ? `${urlWithoutProtocol}twitter`
    : urlWithoutProtocol

  const cachedResult = cache.get(cacheKey)
  if (cachedResult) {
    return Promise.resolve(cachedResult)
  }
  // If a request about the same url is already running, return a
  // Promise that'll resolve with that request's data
  const pendingRequest = pendingRequests.get(cacheKey)
  if (pendingRequest) {
    return Promise.resolve(pendingRequest)
  }

  const request = fetch(
    `${urls[environment]}?${queryString.stringify({
      url,
      // if Twitter, add flag
      ...(isTwitterLinkPreview && { is_twitter_preview: true }),
    })}`,
  )
    .then((response) => response.json())
    .then((urlData: ResultType) => {
      cache.set(cacheKey, urlData)
      pendingRequests.delete(cacheKey)

      return urlData
    })

  pendingRequests.set(cacheKey, request)

  return request
}

/**
 * Test-only: clears the module-level result and pending-request caches so
 * consecutive composer renders in one test file don't serve a previous
 * render's scrape results.
 */
function clearCache(): void {
  cache.clear()
  pendingRequests.clear()
}

export default {
  scrape,
  clearCache,
}
